import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, formatError } from "@/lib/api";
import { AU_STATES } from "@/lib/constants";

const EMPTY = { name: "", abn: "", email: "", phone: "", website: "", address: { street: "", suburb: "", state: "", postcode: "" },
  is_recruitment_company: true, is_approved: true, active: true, notes: "" };

export function Field({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;
}

export function DeleteButton({ onConfirm, testId }) {
  const [armed, setArmed] = useState(false);
  return armed ? (
    <div className="flex gap-2 mr-auto">
      <Button type="button" variant="destructive" size="sm" onClick={onConfirm} data-testid={`${testId}-confirm`}>Confirm delete</Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setArmed(false)}>Cancel</Button>
    </div>
  ) : (
    <Button type="button" variant="ghost" size="sm" className="mr-auto text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setArmed(true)} data-testid={testId}>Delete</Button>
  );
}

export function CompanyFormDialog({ initial, onClose, onSaved }) {
  const { canWrite, isAdmin } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;

  useEffect(() => {
    if (isNew) return setForm(EMPTY);
    api.get(`/companies/${initial.id}`).then((r) => setForm({ ...EMPTY, ...r.data, address: { ...EMPTY.address, ...r.data.address } }))
      .catch((e) => { toast.error(formatError(e)); onClose(); });
  }, [initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, address: { ...form.address, state: form.address.state || null } };
      if (isNew) await api.post("/companies", body);
      else await api.put(`/companies/${initial.id}`, body);
      toast.success(isNew ? "Company created" : "Company saved");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try { await api.delete(`/companies/${initial.id}`); toast.success("Company deleted"); onSaved(); } catch (e) { toast.error(formatError(e)); }
  };

  const ro = !canWrite;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New recruitment company" : form?.name || "Company"}</DialogTitle></DialogHeader>
        {!form ? <div className="p-8 flex justify-center"><Spinner /></div> : (
          <form onSubmit={save} className="space-y-4">
            <fieldset disabled={ro} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name *" className="sm:col-span-2"><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="company-name-input" /></Field>
              <Field label="ABN"><Input value={form.abn || ""} onChange={(e) => set("abn", e.target.value)} placeholder="11 digits" data-testid="company-abn-input" /></Field>
              <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="company-email-input" /></Field>
              <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="company-phone-input" /></Field>
              <Field label="Website"><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="company-website-input" /></Field>
              <Field label="Street" className="sm:col-span-2"><Input value={form.address.street || ""} onChange={(e) => setAddr("street", e.target.value)} data-testid="company-street-input" /></Field>
              <Field label="Suburb"><Input value={form.address.suburb || ""} onChange={(e) => setAddr("suburb", e.target.value)} data-testid="company-suburb-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="State">
                  <Select value={form.address.state || ""} onValueChange={(v) => setAddr("state", v)} disabled={ro}>
                    <SelectTrigger data-testid="company-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>{AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Postcode"><Input value={form.address.postcode || ""} onChange={(e) => setAddr("postcode", e.target.value)} maxLength={4} data-testid="company-postcode-input" /></Field>
              </div>
              <label className="flex items-center gap-3 text-sm text-slate-700"><Switch checked={form.is_approved} onCheckedChange={(v) => set("is_approved", v)} data-testid="company-approved-switch" /> Approved</label>
              <label className="flex items-center gap-3 text-sm text-slate-700"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="company-active-switch" /> Active</label>
              <Field label="Notes" className="sm:col-span-2"><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="company-notes-input" /></Field>
            </fieldset>
            {!isNew && <p className="text-xs text-slate-400" data-testid="company-last-changed">Last changed by {form.updated_by || "—"} · {fmtDateTime(form.updated_at)}</p>}
            <DialogFooter className="gap-2">
              {!isNew && isAdmin && <DeleteButton onConfirm={remove} testId="company-delete-button" />}
              <Button type="button" variant="outline" onClick={onClose} data-testid="company-cancel-button">{ro ? "Close" : "Cancel"}</Button>
              {!ro && <Button type="submit" disabled={busy} data-testid="company-save-button">{busy && <Spinner className="mr-2" />}Save</Button>}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
